import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass,
  Layers,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  GraduationCap,
} from "lucide-react";
import { Logo } from "../components/Logo";

export default function MasterLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh]">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/40">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between">
          <Logo />
          <a
            href="https://channelpartner.network/signup"
            data-testid="topbar-signup-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold transition-all shadow-lg shadow-orange-500/25"
          >
            Sign Up <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-28 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-start gap-5 md:gap-6"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5E1A] px-3 py-1.5 text-xs font-bold tracking-wide">
            <Sparkles className="w-3.5 h-3.5" />
            REAL ESTATE SALES PARTNERS KE LIYE
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-[#0F172A] leading-[1.05] max-w-3xl">
            Har buyer enquiry ko{" "}
            <span className="text-[#FF5E1A]">focused site visit</span> tak
            pahunchao.
          </h1>
          <p className="text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl">
            ChannelPartner.Network aapki help karta hai buyers ko pehle
            qualify karne mein, sahi project match karne mein, aur
            conversion improve karne mein — lead identity protection ke
            saath.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-10">
          <MasterCard
            testId="cta-self-diagnostics"
            icon={<Compass className="w-6 h-6" />}
            eyebrow="2-min quiz"
            title="Self Diagnostics"
            desc="Pata karo aapki buyer journey mein leakage kaha hai — qualification, matching, site-visit confidence ya follow-up."
            cta="Start Diagnostic"
            onClick={() => navigate("/diagnostics")}
            tone="orange"
          />
          <MasterCard
            testId="cta-100-problems"
            icon={<Layers className="w-6 h-6" />}
            eyebrow="Tinder-style deck"
            title="100 problems jo humne solve kiye"
            desc="Har channel partner ke real frustrations swipe karo — aur dekho hum ek-ek ko kaise solve kar rahe hain."
            cta="View 100 Solutions"
            onClick={() => navigate("/problems")}
            tone="blue"
          />
          <MasterCard
            testId="cta-trainings"
            icon={<GraduationCap className="w-6 h-6" />}
            eyebrow="Free trainings"
            title="Trainings for your sales team"
            desc="Short guided sessions on real broker challenges. Live ho toh turant join karo, warna calendar mein book karlo."
            cta="View Trainings"
            onClick={() => navigate("/trainings")}
            tone="green"
          />
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#475569]">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#FF5E1A]" />
            Brokers, freelancers, developer sales partners &amp; consultants
            ke liye banaya gaya
          </span>
          <Link
            to="/admin"
            data-testid="admin-entry-link"
            className="text-[#64748B] hover:text-[#FF5E1A] underline underline-offset-4"
          >
            Admin
          </Link>
        </div>
      </div>
    </div>
  );
}

const MasterCard = ({ testId, icon, eyebrow, title, desc, cta, onClick, tone }) => {
  const palettes = {
    orange: {
      iconBg: "bg-[#FFF3ED] text-[#FF5E1A]",
      cta: "text-[#FF5E1A]",
      glow: "radial-gradient(circle, #FF8A4A 0%, transparent 70%)",
    },
    blue: {
      iconBg: "bg-[#EAF2FF] text-[#2A5CB8]",
      cta: "text-[#2A5CB8]",
      glow: "radial-gradient(circle, #7BB4FF 0%, transparent 70%)",
    },
    green: {
      iconBg: "bg-[#E7F7EE] text-[#0E9F5E]",
      cta: "text-[#0E9F5E]",
      glow: "radial-gradient(circle, #6FE0A9 0%, transparent 70%)",
    },
  };
  const p = palettes[tone] || palettes.orange;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testId}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="text-left glass-strong rounded-3xl p-6 md:p-8 group flex flex-col gap-5 relative overflow-hidden"
    >
      <div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: p.glow }}
      />
      <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center ${p.iconBg}`}>
        {icon}
      </div>
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#64748B] font-bold">
          {eyebrow}
        </p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mt-1.5 leading-tight">
          {title}
        </h2>
        <p className="text-[15px] text-[#475569] leading-relaxed mt-3">
          {desc}
        </p>
      </div>
      <div className={`relative inline-flex items-center gap-2 text-base font-semibold ${p.cta} group-hover:gap-3 transition-all`}>
        {cta} <ArrowRight className="w-5 h-5" />
      </div>
    </motion.button>
  );
};
